import React, { useState, useMemo, useEffect } from "react";
import { diffLines } from "diff";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  Tooltip,
  IconButton,
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon } from "@chakra-ui/icons";

/** Normalize line endings so diff/apply match Quill getText() (\\n) and AI output */
const normalizeDocTemplateText = (s) =>
  typeof s === "string" ? s.replace(/\r\n/g, "\n") : "";

/**
 * Walks diffLines output and builds chunks with **origStart / origEnd** indices into
 * the original string (normalized). Apply uses these offsets so we never rely on
 * indexOf(oldText), which can match the wrong occurrence and merge unrelated lines.
 */
const buildDocTemplateDiffChunks = (oldStr, newStr) => {
  const a = normalizeDocTemplateText(oldStr);
  const b = normalizeDocTemplateText(newStr);
  const parts = diffLines(a, b);
  const chunks = [];
  let origPos = 0;
  let i = 0;

  while (i < parts.length) {
    const part = parts[i];
    if (!part.added && !part.removed) {
      chunks.push({ type: "context", text: part.value });
      origPos += part.value.length;
      i++;
      continue;
    }
    const origStart = origPos;
    let oldText = "";
    let newText = "";
    while (i < parts.length && (parts[i].added || parts[i].removed)) {
      if (parts[i].removed) {
        oldText += parts[i].value;
        origPos += parts[i].value.length;
      } else {
        newText += parts[i].value;
      }
      i++;
    }
    const origEnd = origPos;
    const id = `chunk-${chunks.length}`;
    let type = "change";
    if (oldText && !newText) type = "delete";
    else if (!oldText && newText) type = "insert";
    chunks.push({
      type,
      id,
      oldText,
      newText,
      origStart,
      origEnd,
    });
  }
  return chunks;
};

const CONTEXT_LINES = 3;

const ContextBlock = ({ text }) => {
  const lines = text.split("\n");
  // Keep the trailing empty string from split but don't render it as a blank line
  const nonEmptyLines = lines[lines.length - 1] === "" ? lines.slice(0, -1) : lines;

  if (nonEmptyLines.length <= CONTEXT_LINES * 2) {
    return (
      <Box px={3} py={0}>
        {nonEmptyLines.map((line, i) => (
          <Text key={i} fontSize="xs" color="gray.500" whiteSpace="pre-wrap" lineHeight="1.6">
            {line || "\u00A0"}
          </Text>
        ))}
      </Box>
    );
  }

  const firstLines = nonEmptyLines.slice(0, CONTEXT_LINES);
  const lastLines = nonEmptyLines.slice(-CONTEXT_LINES);
  const hiddenCount = nonEmptyLines.length - CONTEXT_LINES * 2;

  return (
    <Box px={3} py={0}>
      {firstLines.map((line, i) => (
        <Text key={`f${i}`} fontSize="xs" color="gray.500" whiteSpace="pre-wrap" lineHeight="1.6">
          {line || "\u00A0"}
        </Text>
      ))}
      <Text
        fontSize="xs"
        color="gray.400"
        textAlign="center"
        py={1}
        borderTop="1px dashed"
        borderBottom="1px dashed"
        borderColor="gray.200"
        my={1}
      >
        ⋯ {hiddenCount} unchanged line{hiddenCount !== 1 ? "s" : ""} ⋯
      </Text>
      {lastLines.map((line, i) => (
        <Text key={`l${i}`} fontSize="xs" color="gray.500" whiteSpace="pre-wrap" lineHeight="1.6">
          {line || "\u00A0"}
        </Text>
      ))}
    </Box>
  );
};

const renderLines = (text, color, prefix) => {
  const lines = text.split("\n");
  const nonEmpty = lines[lines.length - 1] === "" ? lines.slice(0, -1) : lines;
  return nonEmpty.map((line, i) => (
    <Text key={i} fontSize="xs" color={color} whiteSpace="pre-wrap" lineHeight="1.6" px={3} py={0}>
      <Text as="span" userSelect="none" mr={2} opacity={0.5}>
        {prefix}
      </Text>
      {line || "\u00A0"}
    </Text>
  ));
};

const ChangeChunk = ({ chunk, status, onAccept, onReject, onUndo }) => {
  const borderColor =
    status === "accepted"
      ? "green.300"
      : status === "rejected"
        ? "gray.300"
        : "orange.200";

  return (
    <Box my={2} borderLeft="3px solid" borderLeftColor={borderColor} borderRadius="sm">
      {/* Header row */}
      <HStack
        justify="space-between"
        px={3}
        py={1}
        bg={status === "accepted" ? "green.50" : status === "rejected" ? "gray.50" : "orange.50"}
      >
        <Badge
          colorScheme={
            status === "accepted" ? "green" : status === "rejected" ? "gray" : "orange"
          }
          fontSize="10px"
        >
          {status === "accepted" ? "✓ Accepted" : status === "rejected" ? "✗ Rejected" : "Pending"}
        </Badge>
        {!status ? (
          <HStack spacing={1}>
            <Tooltip label="Accept – use the new version" placement="top">
              <IconButton
                size="xs"
                colorScheme="green"
                variant="outline"
                icon={<CheckIcon />}
                aria-label="Accept"
                onClick={onAccept}
              />
            </Tooltip>
            <Tooltip label="Reject – keep original" placement="top">
              <IconButton
                size="xs"
                colorScheme="red"
                variant="outline"
                icon={<CloseIcon />}
                aria-label="Reject"
                onClick={onReject}
              />
            </Tooltip>
          </HStack>
        ) : (
          <Button size="xs" variant="ghost" colorScheme="gray" onClick={onUndo}>
            Undo
          </Button>
        )}
      </HStack>

      {/* Old text (removed) — hidden when accepted */}
      {status !== "accepted" && chunk.oldText && (
        <Box bg="red.50" py={1}>
          {renderLines(chunk.oldText, "red.700", "−")}
        </Box>
      )}

      {/* New text (added) — hidden when rejected */}
      {status !== "rejected" && chunk.newText && (
        <Box bg="green.50" py={1}>
          {renderLines(chunk.newText, "green.700", "+")}
        </Box>
      )}
    </Box>
  );
};

/**
 * DocDiffViewer
 *
 * Props:
 *   isOpen        – modal visibility
 *   onClose       – called when user cancels (document lock is released by caller)
 *   originalText  – original document plain text
 *   newText       – AI-generated document plain text
 *   isLoading     – true while AI is generating
 *   onApply(text) – called with the final accepted text when user clicks "Apply to Doc"
 */
const DocDiffViewer = ({
  isOpen,
  onClose,
  originalText,
  newText,
  isLoading,
  onApply,
}) => {
  const [chunkStatuses, setChunkStatuses] = useState({});

  // Reset when a new diff arrives
  useEffect(() => {
    setChunkStatuses({});
  }, [originalText, newText]);

  const diffChunks = useMemo(() => {
    if (!originalText || !newText) return [];
    return buildDocTemplateDiffChunks(originalText, newText);
  }, [originalText, newText]);

  const changeChunks = useMemo(
    () => diffChunks.filter((c) => c.type !== "context"),
    [diffChunks],
  );

  const resolvedCount = Object.keys(chunkStatuses).length;
  const totalChanges = changeChunks.length;
  const allResolved = !isLoading && resolvedCount >= totalChanges;

  const accept = (id) => setChunkStatuses((s) => ({ ...s, [id]: "accepted" }));
  const reject = (id) => setChunkStatuses((s) => ({ ...s, [id]: "rejected" }));
  const undo = (id) =>
    setChunkStatuses((s) => {
      const next = { ...s };
      delete next[id];
      return next;
    });

  const acceptAll = () => {
    const s = {};
    changeChunks.forEach((c) => { s[c.id] = "accepted"; });
    setChunkStatuses(s);
  };

  const rejectAll = () => {
    const s = {};
    changeChunks.forEach((c) => { s[c.id] = "rejected"; });
    setChunkStatuses(s);
  };

  const handleApply = () => {
    const finalText = diffChunks
      .map((chunk) => {
        if (chunk.type === "context") return chunk.text;
        const status = chunkStatuses[chunk.id] ?? "rejected";
        return status === "accepted" ? chunk.newText : chunk.oldText;
      })
      .join("");
    // Also pass accepted (oldText→newText) pairs so the caller can do
    // surgical replacements and preserve existing editor formatting.
    const acceptedChanges = diffChunks
      .filter((c) => c.type !== "context" && chunkStatuses[c.id] === "accepted")
      .map((c) => ({
        oldText: c.oldText,
        newText: c.newText,
        origStart: c.origStart,
        origEnd: c.origEnd,
        type: c.type,
      }));
    onApply(finalText, acceptedChanges);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      scrollBehavior="inside"
      closeOnOverlayClick={false}
    >
      <ModalOverlay style={{ zIndex: 10010 }} />
      <ModalContent maxH="90vh" containerProps={{ zIndex: 10011 }}>
        <ModalHeader pb={1}>
          <VStack align="start" spacing={0}>
            <Text>Review AI Changes</Text>
            <Text fontSize="xs" fontWeight="normal" color="gray.500">
              {isLoading
                ? "AI is scanning document and forms…"
                : totalChanges === 0
                  ? "No changes found"
                  : `${totalChanges} change${totalChanges !== 1 ? "s" : ""} · ${resolvedCount} of ${totalChanges} resolved`}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        {/* Accept All / Reject All toolbar */}
        {!isLoading && totalChanges > 0 && (
          <HStack px={6} pb={2} spacing={2}>
            <Button size="xs" colorScheme="green" variant="outline" onClick={acceptAll}>
              Accept All
            </Button>
            <Button size="xs" colorScheme="red" variant="outline" onClick={rejectAll}>
              Reject All
            </Button>
          </HStack>
        )}

        <ModalBody overflowY="auto" fontFamily="mono">
          {isLoading ? (
            <Box py={12} textAlign="center">
              <Spinner size="lg" color="blue.500" />
              <Text mt={4} color="gray.600" fontFamily="body">
                AI is scanning your document and forms…
              </Text>
              <Text mt={1} fontSize="sm" color="gray.400" fontFamily="body">
                This may take a minute
              </Text>
            </Box>
          ) : totalChanges === 0 && newText ? (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              No changes were generated — the document already matches the form
              information.
            </Alert>
          ) : (
            diffChunks.map((chunk, i) =>
              chunk.type === "context" ? (
                <ContextBlock key={i} text={chunk.text} />
              ) : (
                <ChangeChunk
                  key={chunk.id}
                  chunk={chunk}
                  status={chunkStatuses[chunk.id]}
                  onAccept={() => accept(chunk.id)}
                  onReject={() => reject(chunk.id)}
                  onUndo={() => undo(chunk.id)}
                />
              ),
            )
          )}
        </ModalBody>

        <ModalFooter gap={2}>
          <Text fontSize="xs" color="gray.500" mr="auto" fontFamily="body">
            {allResolved && totalChanges > 0
              ? "All changes resolved — ready to apply"
              : isLoading
                ? ""
                : "Review each change before applying"}
          </Text>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleApply}
            isDisabled={!allResolved || totalChanges === 0}
          >
            Apply to Doc
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DocDiffViewer;
